import axios from 'axios';
import { systemCode, xs_languageCode } from '..';
import { deepCopyBet, getConfiguration, getSysDateInFormat, oddToString } from './utils';

function getMaxOdds() {
    const conf = getConfiguration();

    if (conf.pot === 'COMPACT') {
        return 35;
    }

    return 27;
}

function buildRequestPayload(tournaments, bets, range, dateFrom, dateTo) {
    const [from, to] = (dateFrom && dateTo) ? sanitizeDates(dateFrom, dateTo) : [null, null];
    const requestPayload = {
        tourn: [],
        scs: {},
        flt: range,
        from,
        to
    };
    tournaments.forEach(t => requestPayload.tourn.push({sp: t.sportRef, ct: t.categoryRef, tn: t.id}));
    bets.forEach(b => {
        if (requestPayload.scs[b.sportRef] === undefined) {
            requestPayload.scs[b.sportRef] = [];
        }
        const sportBets = requestPayload.scs[b.sportRef];
        if (b.isCompact) {
            b.originalBetsIds.forEach(id => {
                sportBets.push({cs: id, h: 0});
            });
        } else {
            sportBets.push({cs: b.id, h: b.handicap});
        }
    });
    return requestPayload;
}

function parseResponse(data, tournaments, bets) {
    if (Object.keys(data).length === 0) {
        return [[], {}];
    }

    const MAX_ODDS = getMaxOdds();
    
    let aggs = new Set();
    let normal = [];
    let bottom = [];
    for (let i = 0; i < bets.length; i++) {
        const bet = bets[i];
        aggs.add(bet.agId);
        if (bet.odds.length > MAX_ODDS) {
            continue;
        } else if (bet.odds.length <= 25) {
            normal.push(bet);
        } else {
            bottom.push(bet);
        }
    }
    aggs = Array.from(aggs);

    const groups = [[]];
    const groupByBet = {};
    let currentGroup = 0;
    let nOdds = 0;
    normal.forEach(bet => {
        const betOddsLength = bet.odds.length;
        if (nOdds + betOddsLength > MAX_ODDS) {
            currentGroup++;
            groups[currentGroup] = [];
            nOdds = 0;
        }
        groups[currentGroup].push(bet);
        const betId = `${bet.id}_${bet.handicap}`;
        groupByBet[betId] = currentGroup;
        nOdds += betOddsLength;
    });
    bottom.forEach(bet => {
        currentGroup++;
        groups[currentGroup] = [bet];
        const betId = `${bet.id}_${bet.handicap}`;
        groupByBet[betId] = currentGroup;
    });

    tournaments.forEach(t => {
        const matches = data[`${t.sportRef}_${t.categoryRef}_${t.id}`];
        if (matches) {
            matches.forEach(m => {
                m.desc = m.dsl[xs_languageCode];

                const mBetsById = new Map();
                m.scs.forEach(b => mBetsById.set(`${b.cs}_${b.h}`, b));

                m.scs = bets.map(bet => {
                    const betCopy = deepCopyBet(bet);
                    
                    if (bet.isCompact) {
                        betCopy.odds.forEach(odd => {
                            // This odd of the compacted bet originally belonged to the bet with id equal to odd.betId
                            const betId = `${odd.betId}_0`;
                            if (mBetsById.has(betId)) {
                                // Retrieve the bet with the odds corresponding to the original bet to which the odd belonged
                                const mBet = mBetsById.get(betId);
                                // Create a map of odds. Since the bet is compated, we have the same ID across multiple odds
                                // so we used bet ID and odd ID as a unique ID.
                                const mBetOddById = new Map();
                                mBet.eqs.forEach(o => mBetOddById.set(`${mBet.cs}_${o.ce}`, o));

                                // Check if the odd exists in the map (i.e. the odd is not null).
                                const oddId = `${odd.betId}_${odd.id}`;
                                if (mBetOddById.has(oddId)) {
                                    // There was a match, so I copy the actual odd value to odd belonging to the compacted bet
                                    const oddValue = mBetOddById.get(oddId)?.q ?? null;
                                    odd.value = oddToString(oddValue);
                                }
                            }
                        });
                    } else {
                        const betId = `${bet.id}_${bet.handicap}`;
                        if (mBetsById.has(betId)) {
                            const mBet = mBetsById.get(betId);
                            if (betCopy.odds.length > 0) {
                                betCopy.odds.forEach(odd => {
                                    odd.value = oddToString(mBet.eqs.find(o => o.ce === odd.id).q);
                                });
                            } else {
                                betCopy.odds = mBet.eqs.map(odd => {
                                    return {
                                        id: odd.ce,
                                        desc: odd.dsl[xs_languageCode],
                                        value: oddToString(odd.q)
                                    };
                                });
                            }
                        }
                    }
                    
                    return betCopy;
                });
            });
            t.events = matches;
        }else{
            t.events = [];
        }
    });
    
    const result = [[]];
    groups.forEach((g, i) => {
        tournaments.forEach(t => {
            t.events.forEach(m => {
                const filteredBets = m.scs.filter(bet => {
                    const betId = `${bet.id}_${bet.handicap}`;
                    return groupByBet[betId] === i;
                });
                const matchCopy = {...m, scs: filteredBets};
                if (result[i] === undefined) {
                    result.push([]);
                }
                const tournament = result[i].find(tourn => tourn.id === t.id);
                if (tournament === undefined) {
                    result[i].push({...t, events: [matchCopy]});
                } else {
                    tournament.events.push(matchCopy);
                }
            });
        });
    });

    return [result, groupByBet];
}

function validateDate(date) {
    const regEx = /^\d{4}-\d{2}-\d{2}$/;
    if (!date.match(regEx)) {
        return false;
    }
    const d = new Date(date);
    const time = d.getTime();
    if(isNaN(time)) {
        return false;
    }
    return d.toISOString().slice(0,10) === date;
}

function sanitizeDates(dateFrom, dateTo) {
    const today = getSysDateInFormat('YYYY-MM-DD', 0);
    const tomorrow = getSysDateInFormat('YYYY-MM-DD', 1);
    try {
        if (!validateDate(dateFrom) || dateFrom < today) {
            dateFrom = today;
        }
    } catch(e) {
        dateFrom = today;
    }
    try {
        if (!validateDate(dateTo) || dateTo < dateFrom) {
            dateFrom = today;
            dateTo = tomorrow;
        }
    } catch(e) {
        dateFrom = today;
        dateTo = tomorrow;
    }
    dateFrom = dateFrom.replace(/-/g, '') + ' 00:00';
    dateTo = dateTo.replace(/-/g, '') + ' 23:59';
    return [dateFrom, dateTo];
}

export function fetchBetsForTournaments(tournaments, bets, range, dateFrom, dateTo, callback) {
    const url = getConfiguration().api.getBetsForTournaments;
    const params = {
        systemCode,
        lingua: xs_languageCode,
        hash: '',
    };
    const data = buildRequestPayload(tournaments, bets, range, dateFrom, dateTo);
    axios.post(url, data, {headers:{'Content-Type': 'application/json; charset=utf-8'}, params}).then(response => {
        callback(parseResponse(response.data, tournaments, bets));
    }).catch(error => {
        console.error(error);
    });
}

export function printExtraBets(prematchData, data, callback) {
    data = JSON.parse(JSON.stringify(data));
    const matchesByTournament = {};
    const tournamentsData = [];
    const betsById = new Set();
    const bets = [];
    data.forEach(avv => {
        const tournamentId = `${avv.is}_${avv.ic}_${avv.it}`;
        if (matchesByTournament[tournamentId] === undefined) {
            const t = prematchData.tournamentByUniqueId[tournamentId];
            if (t == null) {
                return;
            }
            tournamentsData.push(t);
            matchesByTournament[tournamentId] = [];
        }
        matchesByTournament[tournamentId].push(avv);
        avv.scs.forEach(bet => {
            const uniqueId = `${bet.cs}_${bet.h}`;
            if (!betsById.has(uniqueId)) {
                betsById.add(uniqueId);
                bets.push(bet);
            }
        });
    });
    
    const betsToPrint = new Set()
    const betsData = [];
    tournamentsData.forEach(t => {
        t.aggregate.forEach(agg => {
            agg.scommesse.forEach(bet => {
                const betId = `${bet.id}_${bet.handicap}`;
                if (betsById.has(betId) && !betsToPrint.has(betId)) {
                    betsToPrint.add(betId);
                    betsData.push(bet);
                }
            })
        });
    });
    callback(parseResponse(matchesByTournament, tournamentsData, betsData));
}
